import { useCallback, useEffect, useRef, useState } from "react";

import { api, describeError } from "../api/client";
import type { Bucket, DocumentRecord, DocumentStatus } from "../api/types";
import { PamesaLoader } from "../components/Loader";
import { BucketPill } from "./Buckets";
import "./documents.css";

const IN_PROGRESS: DocumentStatus[] = ["pending", "extracting", "chunking", "embedding"];

const STATUS_LABEL: Record<DocumentStatus, string> = {
  pending: "En cola",
  extracting: "Leyendo",
  chunking: "Troceando",
  embedding: "Indexando",
  indexed: "Indexado",
  failed: "Ha fallado",
};

const ACCEPT = ".pdf,.docx,.xlsx,.txt,.md,.csv,.png,.jpg,.jpeg,.tif,.tiff";

interface Props {
  bucket: Bucket;
  canManage: boolean;
  isAdmin: boolean;
  onBack: () => void;
  /** Lets the shell refresh the bucket list (counts change with uploads/deletes). */
  onChanged: () => Promise<void>;
}

export function BucketDetail({ bucket, canManage, isAdmin, onBack, onChanged }: Props) {
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(bucket.name);
  const fileInput = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    setDocuments(await api.documents(bucket.id));
  }, [bucket.id]);

  useEffect(() => {
    reload().catch((e: unknown) => setError(describeError(e)));
  }, [reload]);

  // Ingestion runs in the worker, so the list is polled while anything is moving.
  // Polling stops as soon as nothing is in progress, rather than running forever.
  useEffect(() => {
    if (!documents.some((document) => IN_PROGRESS.includes(document.status))) return;
    const timer = setInterval(() => {
      reload().catch(() => undefined);
    }, 2500);
    return () => clearInterval(timer);
  }, [documents, reload]);

  async function upload(files: FileList | File[] | null) {
    if (!files || files.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);

    const accepted: string[] = [];
    const failed: string[] = [];
    for (const file of Array.from(files)) {
      try {
        const result = await api.upload(bucket.id, file);
        accepted.push(
          result.duplicate
            ? `${file.name}: ya estaba subido`
            : `${file.name}: subido, procesando`,
        );
      } catch (e: unknown) {
        failed.push(`${file.name}: ${describeError(e)}`);
      }
    }

    if (accepted.length > 0) setNotice(accepted.join(" · "));
    if (failed.length > 0) setError(failed.join(" · "));
    setBusy(false);
    if (fileInput.current) fileInput.current.value = "";
    await reload().catch(() => undefined);
    await onChanged().catch(() => undefined);
  }

  async function removeBucket() {
    setConfirmingDelete(false);
    setError(null);
    try {
      await api.deleteBucket(bucket.id);
      // onChanged refreshes the shell's bucket list; the shell drops this view
      // when the bucket is gone, and onBack makes that immediate.
      await onChanged().catch(() => undefined);
      onBack();
    } catch (e: unknown) {
      setError(describeError(e));
    }
  }

  async function rename() {
    const value = newName.trim();
    if (!value || value === bucket.name) {
      setRenaming(false);
      return;
    }
    setError(null);
    try {
      await api.renameBucket(bucket.id, value);
      setRenaming(false);
      setNotice(`Colección renombrada a «${value}».`);
      // onChanged refreshes the shell's bucket list, which re-renders this view
      // with the fresh bucket, so the title updates without local state.
      await onChanged().catch(() => undefined);
    } catch (e: unknown) {
      setError(describeError(e));
    }
  }

  async function remove(document: DocumentRecord) {
    setError(null);
    try {
      await api.deleteDocument(document.id);
      setNotice(`${document.filename}: eliminado`);
      await reload();
      await onChanged().catch(() => undefined);
    } catch (e: unknown) {
      setError(describeError(e));
    }
  }

  const visible = query.trim()
    ? documents.filter((document) =>
        document.filename.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : documents;

  return (
    <div className="docs">
      <header className="page-head">
        <nav className="breadcrumb" aria-label="Ruta">
          <button type="button" className="breadcrumb-link" onClick={onBack}>
            Colecciones
          </button>
          <span className="breadcrumb-sep" aria-hidden="true">
            /
          </span>
          <span className="breadcrumb-current">{bucket.name}</span>
        </nav>
        <div className="docs-head-actions">
          {isAdmin && (
            <button
              type="button"
              className="btn btn--danger"
              disabled={busy}
              onClick={() => {
                // Two clicks on purpose: the first only arms the button.
                if (!confirmingDelete) {
                  setConfirmingDelete(true);
                  return;
                }
                void removeBucket();
              }}
              onBlur={() => setConfirmingDelete(false)}
            >
              {confirmingDelete
                ? `¿Eliminar «${bucket.name}» y sus ${documents.length.toLocaleString("es-ES")} documentos?`
                : "Eliminar colección"}
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setNewName(bucket.name);
                setRenaming((open) => !open);
              }}
            >
              {renaming ? "Cancelar" : "Renombrar"}
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="btn"
              onClick={() => setEditingPrompt((open) => !open)}
            >
              Instrucciones
            </button>
          )}
          {canManage && (
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              {busy ? "Subiendo…" : "+ Subir documentos"}
            </button>
          )}
        </div>
      </header>

      <div className="page-body">
        <div className="docs-title-row">
          {renaming ? (
            <form
              className="docs-rename"
              onSubmit={(event) => {
                event.preventDefault();
                void rename();
              }}
            >
              <input
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                required
                maxLength={256}
                autoFocus
                aria-label="Nuevo nombre de la colección"
              />
              <button type="submit" className="btn btn--primary">
                Guardar
              </button>
            </form>
          ) : (
            <h1 className="docs-title">{bucket.name}</h1>
          )}
          <BucketPill bucket={bucket} />
        </div>
        <p className="docs-meta">
          {documents.length.toLocaleString("es-ES")} documento{documents.length === 1 ? "" : "s"}
          {bucket.description && ` · ${bucket.description}`}
        </p>

        {editingPrompt && (
          <PromptEditor
            bucket={bucket}
            onClose={() => setEditingPrompt(false)}
            onSaved={(custom) => {
              setEditingPrompt(false);
              setNotice(
                custom
                  ? `Instrucciones de «${bucket.name}» guardadas. Se aplican a la siguiente pregunta.`
                  : `«${bucket.name}» vuelve a las instrucciones por defecto.`,
              );
            }}
            onError={setError}
          />
        )}

        <div
          className={`docs-toolbar${dragging ? " is-dragging" : ""}`}
          onDragOver={
            canManage
              ? (event) => {
                  event.preventDefault();
                  setDragging(true);
                }
              : undefined
          }
          onDragLeave={canManage ? () => setDragging(false) : undefined}
          onDrop={
            canManage
              ? (event) => {
                  event.preventDefault();
                  setDragging(false);
                  void upload(event.dataTransfer.files);
                }
              : undefined
          }
        >
          <input
            type="search"
            className="docs-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar documentos…"
            aria-label="Buscar documentos"
          />
          {canManage && (
            <button
              type="button"
              className="docs-dropzone"
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              Arrastra documentos aquí, o haz clic para elegirlos
            </button>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            disabled={busy || !canManage}
            accept={ACCEPT}
            onChange={(event) => void upload(event.target.files)}
          />
        </div>

        {notice && <p className="docs-notice">{notice}</p>}
        {error && (
          <p className="docs-error" role="alert">
            {error}
          </p>
        )}

        {visible.length === 0 ? (
          <p className="docs-empty">
            {documents.length === 0
              ? canManage
                ? "Esta colección está vacía. Sube un PDF, un Word, un Excel o un texto para empezar."
                : "Esta colección está vacía. Pide a un responsable que suba documentación."
              : "Ningún documento coincide con la búsqueda."}
          </p>
        ) : (
          <div className="docs-card">
            <table className="docs-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th className="num">Tamaño</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                  {canManage && <th aria-label="Acciones" />}
                </tr>
              </thead>
              <tbody>
                {visible.map((document) => (
                  <tr key={document.id}>
                    <td className="docs-name">
                      <span title={document.filename}>{document.filename}</span>
                      {document.error && <em className="docs-reason">{document.error}</em>}
                    </td>
                    <td className="docs-type">{fileType(document.filename)}</td>
                    <td className="num">{formatSize(document.size_bytes)}</td>
                    <td className="docs-date">{formatDate(document.created_at)}</td>
                    <td>
                      <span className={`pill ${statusPill(document.status)}`}>
                        {/* El trazo solo mientras algo se mueve de verdad. Un
                            indicador que gira sobre un estado terminal dice que
                            algo pasa cuando no pasa nada. */}
                        {IN_PROGRESS.includes(document.status) && (
                          <PamesaLoader
                            className="pl--fila"
                            label={STATUS_LABEL[document.status]}
                          />
                        )}
                        {STATUS_LABEL[document.status]}
                      </span>
                    </td>
                    {canManage && (
                      <td className="docs-actions">
                        <button
                          type="button"
                          className="docs-delete"
                          aria-label={`Eliminar ${document.filename}`}
                          onClick={() => void remove(document)}
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PromptEditor({
  bucket,
  onClose,
  onSaved,
  onError,
}: {
  bucket: Bucket;
  onClose: () => void;
  onSaved: (custom: boolean) => void;
  onError: (message: string) => void;
}) {
  const [template, setTemplate] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .bucketPrompt(bucket.id)
      .then((body) => setTemplate(body.prompt_template ?? ""))
      .catch((e: unknown) => onError(describeError(e)));
  }, [bucket.id, onError]);

  async function save() {
    if (busy || template === null) return;
    setBusy(true);
    try {
      const value = template.trim() || null;
      await api.setBucketPrompt(bucket.id, value);
      onSaved(value !== null);
    } catch (e: unknown) {
      onError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card docs-editor" aria-label={`Instrucciones de ${bucket.name}`}>
      <header className="docs-editor-head">
        <div>
          <span className="eyebrow">Instrucciones del asistente</span>
          <h2 className="docs-editor-title">{bucket.name}</h2>
        </div>
        <button type="button" className="btn btn--quiet" onClick={onClose}>
          Cerrar
        </button>
      </header>

      <p className="docs-editor-hint">
        Definen cómo responde el asistente cuando la pregunta usa esta colección. En
        blanco, se aplican las instrucciones por defecto. Los cambios valen a partir
        de la siguiente pregunta, sin despliegues.
      </p>

      {template === null ? (
        <p className="docs-empty">Cargando…</p>
      ) : (
        <textarea
          value={template}
          onChange={(event) => setTemplate(event.target.value)}
          rows={12}
          maxLength={20000}
          placeholder="En blanco: instrucciones por defecto."
          aria-label="Plantilla de instrucciones"
        />
      )}

      <div className="docs-editor-actions">
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void save()}
          disabled={busy || template === null}
        >
          {busy ? "Guardando…" : "Guardar"}
        </button>
        {template && (
          <button type="button" className="btn" onClick={() => setTemplate("")} disabled={busy}>
            Vaciar (volver a las de defecto)
          </button>
        )}
      </div>
    </section>
  );
}

function statusPill(status: DocumentStatus): string {
  if (status === "indexed") return "pill--ok";
  if (status === "failed") return "pill--fault";
  return "pill--warn";
}

function fileType(filename: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot === -1 || dot === filename.length - 1) return "—";
  return filename.slice(dot + 1).toUpperCase();
}


function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
